import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useColors, useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { getBillingSubscriptionApiRequest } from '@/services/api/billing-api';
import { getUserApiRequest } from '@/services/api/user-api';
import cookies from '@/utils/cookies';
import { useStore } from '@/store';

type WarningState = null | 30 | 50 | 70 | 100 | 'blocked';

export const useFreeEventsLimit = (emitClose: () => void) => {
  const { t } = useI18n();
  const $colors = useColors();
  const store = useStore();
  const router = useRouter();

  const active = ref(false);
  const tachometerIcon = ref('');
  const tachometerColor = ref('');
  const title = ref('');
  const modalTitle = ref('');
  const text = ref('');
  const cancelIcon = ref('');
  const cancelText = ref('');
  const warningState = ref<WarningState>(null);

  const isAllowCloseButton = computed(() =>
    ([30, 50, 70] as WarningState[]).includes(warningState.value),
  );

  const showLogOutButton = computed(
    () => warningState.value === 100 || warningState.value === 'blocked',
  );

  const warningStateCalculate = (): WarningState => {
    const { eventsUsed, eventsIncluded } = store.billing;
    const percent = (eventsUsed * 100) / eventsIncluded;
    const warningStateCookie = cookies.get('hide-dialog-limit-is-reached') as string;
    const { blockedCampaigns } = store.user;

    if (blockedCampaigns) {
      return 'blocked';
    }

    if (percent >= 100) {
      return 100;
    }

    if (percent <= 100 && percent >= 70 && !['70'].includes(warningStateCookie)) {
      return 70;
    }

    if (percent < 70 && percent >= 50 && !['50', '70'].includes(warningStateCookie)) {
      return 50;
    }

    if (percent < 50 && percent >= 30 && !['30', '50', '70'].includes(warningStateCookie)) {
      return 30;
    }

    return null;
  };

  const updateText = () => {
    switch (warningState.value) {
      case 30: {
        modalTitle.value = t('Overage Notice');
        tachometerIcon.value = 'fa-tachometer-alt-slow';
        cancelIcon.value = 'fas fa-times';
        cancelText.value = t('Not Now');
        title.value = t('You have used more than 30 000 of free events.');
        text.value = t(
          'On {0} plan you are provided with 100 000 free events on 30 days.<br>The traffic in your campaigns will be stopped, if all the free events are used until the end of your billing period.<br>Upgrade the subscription to continue tracking your campaigns with BeMob.<br>To see the exact amount of used events visit the <button id="goToSubscription">Subscription</button> section.<br>Contact our live chat support if you have any questions concerning your subscription.',
          [store.billing.planId],
        );
        tachometerColor.value = $colors.green;
        return;
      }
      case 50: {
        modalTitle.value = t('Overage Notice');
        tachometerIcon.value = 'fa-tachometer-alt-average';
        cancelIcon.value = 'fas fa-times';
        cancelText.value = t('Not Now');
        title.value = t('You have used more than 50 000 of free events.');
        text.value = t(
          'On {0} plan you are provided with 100 000 free events on 30 days.<br>The traffic in your campaigns will be stopped, if all the free events are used before your billing period ends.<br>Upgrade the subscription to continue tracking your campaigns with BeMob.<br>To see the exact amount of used events visit the <button id="goToSubscription">Subscription</button> section.<br>Contact our live chat support if you have any questions concerning your subscription.',
          [store.billing.planId],
        );
        tachometerColor.value = $colors.blue;
        return;
      }
      case 70: {
        modalTitle.value = t('Overage Notice');
        tachometerIcon.value = 'fa-tachometer-alt-fast';
        cancelIcon.value = 'fas fa-times';
        cancelText.value = t('Not Now');
        title.value = t('You have used more than 70 000 of free events.');
        text.value = t(
          'On {0} plan you are provided with 100 000 free events on 30 days.<br>The traffic in your campaigns will be stopped, if all the free events are used until the end of your billing period.<br>Upgrade the subscription to continue tracking your campaigns with BeMob.<br>To see the exact amount of used events visit the <button id="goToSubscription">Subscription</button> section.<br>Contact our live chat support if you have any questions concerning your subscription.',
          [store.billing.planId],
        );
        tachometerColor.value = $colors.sunglow;
        return;
      }
      case 100: {
        modalTitle.value = t('Overage Notice');
        tachometerIcon.value = 'fa-tachometer-alt-fastest';
        title.value = t('You reached the limit of free events.');
        text.value = t(
          'Upgrade the subscription to continue your work with BeMob as the traffic will be paused soon.<br>Contact our live chat support if you have any further questions.',
        );
        tachometerColor.value = $colors.red;
        return;
      }
      case 'blocked': {
        modalTitle.value = t('Traffic Stopped');
        tachometerIcon.value = 'fa-user-lock';
        title.value = t('Free events finished.');
        text.value = t(
          'Traffic in your campaigns is stopped as you have used all the free events.<br>Upgrade subscription to continue your work with BeMob.<br>Traffic will be automatically resumed after the subscription cost is paid.',
        );
        tachometerColor.value = $colors.red;
      }
    }
  };

  const bindSubscriptionLink = () => {
    nextTick(() => {
      const subscriptionLink = document.querySelector('#goToSubscription');
      subscriptionLink?.addEventListener('click', goToSubscription);
    });
  };

  const unbindSubscriptionLink = () => {
    document.querySelector('#goToSubscription')?.removeEventListener('click', goToSubscription);
  };

  const warningStateUpdate = () => {
    const { hasSuccessPayment, hideEventsLimitIsReached } = store.user;

    if (hasSuccessPayment === 1 || hideEventsLimitIsReached) {
      active.value = false;
      return;
    }

    warningState.value = warningStateCalculate();

    if (warningState.value) {
      updateText();
      active.value = true;
      bindSubscriptionLink();
    } else {
      active.value = false;
    }
  };

  const onPanelBlockedChanged = async () => {
    const user = await getUserApiRequest();
    store.mergeUser(user);
    store.setBilling(await getBillingSubscriptionApiRequest());
    warningStateUpdate();
  };

  const close = () => {
    if (isAllowCloseButton.value) {
      active.value = false;
      cookies.set('hide-dialog-limit-is-reached', warningState.value, { expires: '72h' });
    }
  };

  const actionBeforeLogout = () => {
    emitClose();
  };

  const goToSubscription = () => {
    if (isAllowCloseButton.value) {
      close();
      router.push('/app/profile/subscription');
    } else {
      active.value = false;
      $emitter.emit('open-dialog', {
        name: 'ChangePlanDialog',
        data: {
          registration: true,
          dontClose: true,
        },
      });
    }
  };

  let stopPanelBlockedWatch: (() => void) | null = null;

  onMounted(() => {
    stopPanelBlockedWatch = watch(
      () => store.panelBlocked,
      () => {
        void onPanelBlockedChanged();
      },
    );
  });

  watch(
    () => store.billing,
    () => {
      warningStateUpdate();
    },
    { deep: true },
  );

  onBeforeUnmount(() => {
    stopPanelBlockedWatch?.();
    stopPanelBlockedWatch = null;
    unbindSubscriptionLink();
  });

  return {
    t,
    active,
    tachometerIcon,
    tachometerColor,
    title,
    modalTitle,
    text,
    cancelIcon,
    cancelText,
    warningState,
    isAllowCloseButton,
    showLogOutButton,
    close,
    goToSubscription,
    actionBeforeLogout,
  };
};
